import { useState, useEffect } from "react";

// ─────────────────────────────────────────────
// DESIGN TOKENS  — dark nightlife aesthetic
// ─────────────────────────────────────────────
const T = {
  bg:       "#0A0A0F",
  surface:  "#111118",
  card:     "#16161F",
  border:   "#1E1E2E",
  borderHi: "#2E2E4E",
  gold:     "#D4AF37",
  goldDim:  "#8B7020",
  blue:     "#3B82F6",
  purple:   "#8B5CF6",
  green:    "#10B981",
  red:      "#EF4444",
  yellow:   "#F59E0B",
  text:     "#F0F0F8",
  textMid:  "#8B8BA8",
  textDim:  "#4A4A6A",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${T.bg}; color: ${T.text}; font-family: 'DM Sans', sans-serif; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: ${T.surface}; }
  ::-webkit-scrollbar-thumb { background: ${T.borderHi}; border-radius: 4px; }

  @keyframes pulse-gold {
    0%, 100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.3); }
    50%       { box-shadow: 0 0 0 8px rgba(212,175,55,0); }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
  .fade-up { animation: fadeUp 0.4s ease both; }
  .fade-up-1 { animation: fadeUp 0.4s 0.05s ease both; }
  .fade-up-2 { animation: fadeUp 0.4s 0.10s ease both; }
  .fade-up-3 { animation: fadeUp 0.4s 0.15s ease both; }
  .fade-up-4 { animation: fadeUp 0.4s 0.20s ease both; }
  .pulse-gold { animation: pulse-gold 2s infinite; }

  .row-hover { transition: background 0.15s; }
  .row-hover:hover { background: rgba(212,175,55,0.04) !important; }

  .tab-btn { transition: all 0.2s; }
  .tab-btn:hover { color: ${T.text} !important; }

  .stat-card { transition: transform 0.2s, box-shadow 0.2s; }
  .stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(0,0,0,0.4); }

  .btn-primary {
    background: linear-gradient(135deg, ${T.gold}, #B8962E);
    color: #000;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .btn-primary:hover { filter: brightness(1.1); transform: translateY(-1px); }
  .btn-ghost {
    background: transparent;
    color: ${T.textMid};
    border: 1px solid ${T.border};
    border-radius: 10px;
    padding: 10px 20px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .btn-ghost:hover { border-color: ${T.borderHi}; color: ${T.text}; }
  .badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; }
  select, input { background: ${T.surface}; color: ${T.text}; border: 1px solid ${T.border}; border-radius: 10px; padding: 10px 14px; font-family: 'DM Sans', sans-serif; font-size: 14px; outline: none; transition: border 0.2s; }
  select:focus, input:focus { border-color: ${T.gold}; }
  option { background: ${T.surface}; }
`;

// ─────────────────────────────────────────────
// MOCK DATA
// ─────────────────────────────────────────────
const CITIES = ["São Paulo", "Rio de Janeiro", "Belo Horizonte", "Curitiba", "Porto Alegre"];
const VENUES = [
  { id: "v1", name: "Club Noir", city: "São Paulo",       monthlyRevenue: 18400 },
  { id: "v2", name: "Lux Underground", city: "São Paulo", monthlyRevenue: 14200 },
  { id: "v3", name: "Ballroom RJ", city: "Rio de Janeiro",monthlyRevenue: 21000 },
  { id: "v4", name: "Aurora Club", city: "Curitiba",      monthlyRevenue:  9800 },
  { id: "v5", name: "Neon Garden", city: "Porto Alegre",  monthlyRevenue: 11300 },
  { id: "v6", name: "The Basement", city: "Belo Horizonte", monthlyRevenue: 7600 },
];

const COMMISSION_RATE = 0.06;
const TAX_RATE        = 0.18;   // impostos
const CARD_FEE_RATE   = 0.035;  // taxa de cartão

function calcNet(gross) {
  const taxes = gross * TAX_RATE;
  const cardFee = gross * CARD_FEE_RATE;
  return gross - taxes - cardFee;
}
function calcCommission(gross) {
  return calcNet(gross) * COMMISSION_RATE;
}

const ALL_AFFILIATES = [
  {
    id: "a1", type: "artist", name: "DJ Fever", avatar: "🎧",
    city: "São Paulo", venues: [],
    status: "active", joinedAt: "2024-09-01",
    instagramFollowers: 84000,
    monthlyHistory: [9200, 11400, 14800, 16200, 18400, 21000],
    description: "DJ residente há 8 anos, especialista em techno e house music.",
    contact: "djfever@email.com",
  },
  {
    id: "a2", type: "artist", name: "Banda Vórtex", avatar: "🎸",
    city: "Rio de Janeiro", venues: [],
    status: "active", joinedAt: "2024-11-15",
    instagramFollowers: 127000,
    monthlyHistory: [14200, 16800, 19200, 20100, 21000, 22400],
    description: "Banda de rock alternativo com presença em festivais nacionais.",
    contact: "vortex@email.com",
  },
  {
    id: "a3", type: "influencer", name: "@nightlife.sp", avatar: "✨",
    city: "São Paulo", venues: ["v1", "v2"],
    status: "active", joinedAt: "2025-01-10",
    instagramFollowers: 312000,
    monthlyHistory: [8400, 11200, 14600, 16800, 22400, 25800],
    description: "Criadora de conteúdo focada em vida noturna e entretenimento.",
    contact: "nightlifesp@gmail.com",
  },
  {
    id: "a4", type: "influencer", name: "@rj.noite", avatar: "🌙",
    city: "Rio de Janeiro", venues: ["v3"],
    status: "active", joinedAt: "2025-02-01",
    instagramFollowers: 198000,
    monthlyHistory: [0, 0, 7400, 12000, 18600, 21000],
    description: "Influencer de entretenimento noturno do Rio.",
    contact: "rjnoite@email.com",
  },
  {
    id: "a5", type: "artist", name: "MC Pulse", avatar: "🎤",
    city: "Belo Horizonte", venues: [],
    status: "pending", joinedAt: "2025-02-20",
    instagramFollowers: 56000,
    monthlyHistory: [0, 0, 0, 0, 0, 0],
    description: "MC de funk e trap com shows regulares em casas de BH.",
    contact: "mcpulse@email.com",
  },
  {
    id: "a6", type: "influencer", name: "@clubber.oficial", avatar: "🔥",
    city: "Curitiba", venues: ["v4"],
    status: "inactive", joinedAt: "2024-08-01",
    instagramFollowers: 74000,
    monthlyHistory: [6200, 7400, 8100, 5200, 3400, 0],
    description: "Influencer de moda e nightlife em Curitiba.",
    contact: "clubber@email.com",
  },
  {
    id: "a7", type: "influencer", name: "@porto.after", avatar: "🎵",
    city: "Porto Alegre", venues: ["v5"],
    status: "active", joinedAt: "2025-01-20",
    instagramFollowers: 89000,
    monthlyHistory: [0, 4200, 7800, 9400, 10200, 11300],
    description: "Criador de conteúdo de gastronomia e vida noturna gaúcha.",
    contact: "portoafter@email.com",
  },
];

const MONTHS = ["Set", "Out", "Nov", "Dez", "Jan", "Fev"];

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
function fmt(n) { return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2 }); }
function fmtShort(n) { return n >= 1000 ? `R$ ${(n/1000).toFixed(1)}k` : `R$ ${n.toFixed(0)}`; }

function getAffiliateRevenue(aff) {
  if (aff.type === "artist") {
    const city = CITIES.find(c => c === aff.city);
    const cityRevenue = VENUES.filter(v => v.city === city).reduce((s, v) => s + v.monthlyRevenue, 0);
    return cityRevenue;
  } else {
    return aff.venues.reduce((s, vid) => {
      const v = VENUES.find(vv => vv.id === vid);
      return s + (v ? v.monthlyRevenue : 0);
    }, 0);
  }
}

function StatusBadge({ status }) {
  const cfg = {
    active:   { bg: "rgba(16,185,129,0.15)", color: T.green,  label: "Ativo"    },
    pending:  { bg: "rgba(245,158,11,0.15)", color: T.yellow, label: "Pendente" },
    inactive: { bg: "rgba(239,68,68,0.15)",  color: T.red,    label: "Inativo"  },
  }[status] || {};
  return <span className="badge" style={{ background: cfg.bg, color: cfg.color }}>● {cfg.label}</span>;
}

function TypeBadge({ type }) {
  return type === "artist"
    ? <span className="badge" style={{ background: "rgba(139,92,246,0.15)", color: T.purple }}>🎵 Artista</span>
    : <span className="badge" style={{ background: "rgba(212,175,55,0.15)", color: T.gold }}>⭐ Influencer</span>;
}

// ─────────────────────────────────────────────
// MINI BAR CHART
// ─────────────────────────────────────────────
function MiniBar({ data, color = T.gold, height = 40 }) {
  const max = Math.max(...data, 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height }}>
      {data.map((v, i) => (
        <div key={i} style={{
          flex: 1, height: `${(v / max) * 100}%`,
          background: i === data.length - 1
            ? color
            : `${color}44`,
          borderRadius: "3px 3px 0 0",
          minHeight: v > 0 ? 3 : 0,
          transition: "height 0.4s ease",
        }} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// STAT CARD
// ─────────────────────────────────────────────
function StatCard({ label, value, sub, icon, accent = T.gold, delay = "" }) {
  return (
    <div className={`stat-card fade-up${delay}`} style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: 16,
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        {sub && <span style={{ fontSize: 11, color: accent, fontWeight: 700, background: `${accent}18`, padding: "2px 8px", borderRadius: 20 }}>{sub}</span>}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: T.text, fontFamily: "'DM Serif Display', serif", letterSpacing: -0.5 }}>
        {value}
      </div>
      <div style={{ fontSize: 13, color: T.textMid }}>{label}</div>
    </div>
  );
}

// ─────────────────────────────────────────────
// AFFILIATE DETAIL MODAL
// ─────────────────────────────────────────────
function AffiliateModal({ aff, onClose, onStatusChange }) {
  const revenue = getAffiliateRevenue(aff);
  const net     = calcNet(revenue);
  const comm    = calcCommission(revenue);
  const venues  = aff.type === "influencer" ? aff.venues.map(id => VENUES.find(v => v.id === id)).filter(Boolean) : [];
  const cityVenues = aff.type === "artist" ? VENUES.filter(v => v.city === aff.city) : [];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 999,
      background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: T.card, border: `1px solid ${T.borderHi}`,
        borderRadius: 20, width: "100%", maxWidth: 580,
        maxHeight: "90vh", overflowY: "auto",
        padding: 32,
        animation: "fadeUp 0.25s ease",
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              background: `linear-gradient(135deg, ${T.gold}33, ${T.purple}33)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 28, border: `2px solid ${T.gold}44`,
            }}>{aff.avatar}</div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'DM Serif Display', serif" }}>{aff.name}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <TypeBadge type={aff.type} />
                <StatusBadge status={aff.status} />
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMid, fontSize: 22, cursor: "pointer" }}>✕</button>
        </div>

        {/* Info grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
          {[
            { label: "Cidade", value: aff.city },
            { label: "Seguidores", value: aff.instagramFollowers.toLocaleString("pt-BR") },
            { label: "Membro desde", value: new Date(aff.joinedAt).toLocaleDateString("pt-BR") },
            { label: "Contato", value: aff.contact },
          ].map(item => (
            <div key={item.label} style={{ background: T.surface, borderRadius: 12, padding: "12px 16px" }}>
              <div style={{ fontSize: 11, color: T.textMid, marginBottom: 4 }}>{item.label}</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{item.value}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, marginBottom: 24, padding: "12px 16px", background: T.surface, borderRadius: 12 }}>
          {aff.description}
        </div>

        {/* Locations */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.textMid, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>
            {aff.type === "artist" ? "🏙️ Cidade exclusiva" : "🏠 Estabelecimentos vinculados"}
          </div>
          {aff.type === "artist" ? (
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{aff.city}</div>
              {cityVenues.map(v => (
                <div key={v.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}`, fontSize: 14 }}>
                  <span style={{ color: T.textMid }}>{v.name}</span>
                  <span style={{ color: T.green }}>{fmt(v.monthlyRevenue)}</span>
                </div>
              ))}
            </div>
          ) : venues.map(v => (
            <div key={v.id} style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", background: T.surface, borderRadius: 10, marginBottom: 6 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{v.name}</div>
                <div style={{ fontSize: 12, color: T.textMid }}>{v.city}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 14, color: T.green }}>{fmt(v.monthlyRevenue)}</div>
                <div style={{ fontSize: 11, color: T.textMid }}>receita mensal</div>
              </div>
            </div>
          ))}
          {aff.type === "influencer" && venues.length === 0 && (
            <div style={{ color: T.textDim, fontSize: 13 }}>Nenhum estabelecimento vinculado.</div>
          )}
        </div>

        {/* Commission breakdown */}
        <div style={{ background: `${T.gold}10`, border: `1px solid ${T.gold}33`, borderRadius: 14, padding: 20, marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.gold, marginBottom: 16, textTransform: "uppercase", letterSpacing: 1 }}>
            💰 Comissionamento — Mês atual
          </div>
          {[
            { label: "Receita bruta de assinaturas", value: fmt(revenue), color: T.text },
            { label: `Impostos (${(TAX_RATE*100).toFixed(0)}%)`, value: `− ${fmt(revenue * TAX_RATE)}`, color: T.red },
            { label: `Taxas de cartão (${(CARD_FEE_RATE*100).toFixed(1)}%)`, value: `− ${fmt(revenue * CARD_FEE_RATE)}`, color: T.red },
            { label: "Base líquida", value: fmt(net), color: T.textMid },
          ].map(r => (
            <div key={r.label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, fontSize: 14 }}>
              <span style={{ color: T.textMid }}>{r.label}</span>
              <span style={{ color: r.color, fontWeight: 600 }}>{r.value}</span>
            </div>
          ))}
          <div style={{ borderTop: `1px solid ${T.gold}33`, paddingTop: 12, marginTop: 4, display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>Comissão (6%)</span>
            <span style={{ fontWeight: 800, fontSize: 18, color: T.gold }}>{fmt(comm)}</span>
          </div>
        </div>

        {/* Histórico */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.textMid, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>
            📈 Histórico de receita (6 meses)
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 8, marginBottom: 8 }}>
            {aff.monthlyHistory.map((v, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: T.textDim, marginBottom: 4 }}>{MONTHS[i]}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: v > 0 ? T.green : T.textDim }}>
                  {v > 0 ? fmtShort(v) : "—"}
                </div>
              </div>
            ))}
          </div>
          <MiniBar data={aff.monthlyHistory} color={T.gold} height={48} />
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          {aff.status !== "active" && (
            <button className="btn-primary" style={{ flex: 1 }} onClick={() => onStatusChange(aff.id, "active")}>
              ✓ Ativar Afiliado
            </button>
          )}
          {aff.status === "active" && (
            <button className="btn-ghost" style={{ flex: 1, borderColor: T.red + "55", color: T.red }} onClick={() => onStatusChange(aff.id, "inactive")}>
              Desativar
            </button>
          )}
          {aff.status === "pending" && (
            <button className="btn-ghost" style={{ flex: 1, borderColor: T.red + "55", color: T.red }} onClick={() => onStatusChange(aff.id, "inactive")}>
              Recusar
            </button>
          )}
          <button className="btn-primary" onClick={onClose} style={{ flex: 1 }}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// NEW AFFILIATE MODAL
// ─────────────────────────────────────────────
function NewAffiliateModal({ onClose, onAdd }) {
  const [form, setForm] = useState({
    type: "influencer", name: "", avatar: "⭐",
    city: CITIES[0], venues: [],
    instagramFollowers: "", contact: "", description: "",
  });

  const avatarOptions = { artist: ["🎧","🎸","🎤","🎻","🥁"], influencer: ["✨","🌙","🔥","🎵","💫"] };

  const toggle = (field, val) => {
    const arr = form[field];
    setForm({ ...form, [field]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] });
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div style={{ background: T.card, border: `1px solid ${T.borderHi}`, borderRadius: 20, width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", padding: 32, animation: "fadeUp 0.25s ease" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22 }}>Novo Afiliado</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMid, fontSize: 22, cursor: "pointer" }}>✕</button>
        </div>

        {/* Type selector */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
          {["artist","influencer"].map(t => (
            <button key={t} onClick={() => setForm({ ...form, type: t, avatar: avatarOptions[t][0], venues: [] })} style={{
              padding: "14px", borderRadius: 12, border: `2px solid ${form.type === t ? T.gold : T.border}`,
              background: form.type === t ? `${T.gold}10` : T.surface,
              color: form.type === t ? T.gold : T.textMid,
              fontWeight: 700, cursor: "pointer", fontSize: 14,
              transition: "all 0.2s",
            }}>
              {t === "artist" ? "🎵 Artista" : "⭐ Influencer"}
            </button>
          ))}
        </div>

        {/* Avatar */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: T.textMid, marginBottom: 8 }}>Ícone</div>
          <div style={{ display: "flex", gap: 8 }}>
            {avatarOptions[form.type].map(a => (
              <button key={a} onClick={() => setForm({ ...form, avatar: a })} style={{
                width: 44, height: 44, borderRadius: 10, border: `2px solid ${form.avatar === a ? T.gold : T.border}`,
                background: form.avatar === a ? `${T.gold}15` : T.surface,
                fontSize: 20, cursor: "pointer",
              }}>{a}</button>
            ))}
          </div>
        </div>

        {/* Fields */}
        {[
          { label: "Nome / Handle", key: "name", placeholder: form.type === "influencer" ? "@seu.perfil" : "Nome do artista" },
          { label: "Contato (e-mail)", key: "contact", placeholder: "email@dominio.com" },
          { label: "Seguidores no Instagram", key: "instagramFollowers", placeholder: "84000" },
          { label: "Descrição", key: "description", placeholder: "Breve apresentação..." },
        ].map(f => (
          <div key={f.key} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: T.textMid, marginBottom: 6 }}>{f.label}</div>
            <input value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} style={{ width: "100%" }} />
          </div>
        ))}

        {/* City / Venues */}
        {form.type === "artist" ? (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: T.textMid, marginBottom: 6 }}>Cidade exclusiva</div>
            <select value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} style={{ width: "100%" }}>
              {CITIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        ) : (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: T.textMid, marginBottom: 8 }}>Estabelecimentos vinculados</div>
            {VENUES.map(v => (
              <label key={v.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", cursor: "pointer", fontSize: 14 }}>
                <div onClick={() => toggle("venues", v.id)} style={{
                  width: 20, height: 20, borderRadius: 5, border: `2px solid ${form.venues.includes(v.id) ? T.gold : T.border}`,
                  background: form.venues.includes(v.id) ? T.gold : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, cursor: "pointer",
                }}>
                  {form.venues.includes(v.id) && <span style={{ fontSize: 11, fontWeight: 900, color: "#000" }}>✓</span>}
                </div>
                <span style={{ color: T.text }}>{v.name}</span>
                <span style={{ color: T.textDim, fontSize: 12 }}>— {v.city}</span>
              </label>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancelar</button>
          <button className="btn-primary" style={{ flex: 2 }} onClick={() => {
            if (!form.name) return alert("Informe o nome.");
            const newAff = {
              ...form,
              id: "a" + Date.now(),
              status: "pending",
              joinedAt: new Date().toISOString().slice(0, 10),
              monthlyHistory: [0, 0, 0, 0, 0, 0],
              instagramFollowers: parseInt(form.instagramFollowers) || 0,
            };
            onAdd(newAff);
            onClose();
          }}>
            Cadastrar Afiliado →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// COMMISSION REPORT VIEW
// ─────────────────────────────────────────────
function CommissionReport({ affiliates }) {
  const active = affiliates.filter(a => a.status === "active");
  const rows = active.map(aff => {
    const gross = getAffiliateRevenue(aff);
    const net   = calcNet(gross);
    const comm  = calcCommission(gross);
    return { aff, gross, net, comm };
  }).sort((a, b) => b.comm - a.comm);

  const totalComm = rows.reduce((s, r) => s + r.comm, 0);
  const totalGross = rows.reduce((s, r) => s + r.gross, 0);

  return (
    <div className="fade-up">
      {/* summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 28 }}>
        <StatCard icon="💸" label="Total a pagar em comissões" value={fmt(totalComm)} sub="Este mês" />
        <StatCard icon="📊" label="Receita bruta das unidades" value={fmt(totalGross)} sub="Base de cálculo" delay="-1" />
        <StatCard icon="🧾" label="Taxa média efetiva paga" value={`${((TAX_RATE + CARD_FEE_RATE) * 100).toFixed(1)}%`} sub="Impostos + cartão" delay="-2" />
      </div>

      {/* table */}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
        <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Relatório de Comissões — Fevereiro 2026</span>
          <button className="btn-ghost" style={{ fontSize: 12, padding: "6px 14px" }}>⬇ Exportar CSV</button>
        </div>

        {/* thead */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr", padding: "10px 24px", borderBottom: `1px solid ${T.border}`, fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.8 }}>
          {["Afiliado","Tipo","Receita Bruta","Deduções","Base Líquida","Comissão (6%)"].map(h => <span key={h}>{h}</span>)}
        </div>

        {rows.map(({ aff, gross, net, comm }) => {
          const deduct = gross - net;
          return (
            <div key={aff.id} className="row-hover" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr", padding: "14px 24px", borderBottom: `1px solid ${T.border}`, alignItems: "center", fontSize: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>{aff.avatar}</span>
                <div>
                  <div style={{ fontWeight: 600 }}>{aff.name}</div>
                  <div style={{ fontSize: 11, color: T.textDim }}>{aff.type === "artist" ? aff.city : `${aff.venues.length} estabelec.`}</div>
                </div>
              </div>
              <TypeBadge type={aff.type} />
              <span style={{ color: T.text }}>{fmt(gross)}</span>
              <span style={{ color: T.red }}>−{fmt(deduct)}</span>
              <span style={{ color: T.textMid }}>{fmt(net)}</span>
              <span style={{ color: T.gold, fontWeight: 700 }}>{fmt(comm)}</span>
            </div>
          );
        })}

        {/* footer total */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr", padding: "14px 24px", background: `${T.gold}08`, alignItems: "center", fontSize: 14 }}>
          <span style={{ fontWeight: 700, color: T.gold }}>TOTAL</span>
          <span />
          <span style={{ fontWeight: 700 }}>{fmt(totalGross)}</span>
          <span style={{ fontWeight: 700, color: T.red }}>−{fmt(totalGross - rows.reduce((s,r)=>s+r.net,0))}</span>
          <span style={{ fontWeight: 700 }}>{fmt(rows.reduce((s,r)=>s+r.net,0))}</span>
          <span style={{ fontWeight: 800, fontSize: 16, color: T.gold }}>{fmt(totalComm)}</span>
        </div>
      </div>

      {/* methodology note */}
      <div style={{ marginTop: 20, padding: "14px 20px", background: T.surface, borderRadius: 12, fontSize: 13, color: T.textMid, lineHeight: 1.7 }}>
        <strong style={{ color: T.text }}>Metodologia de cálculo:</strong> Receita bruta → subtrai impostos ({(TAX_RATE*100).toFixed(0)}%) + taxas de cartão ({(CARD_FEE_RATE*100).toFixed(1)}%) → base líquida × 6% = comissão do afiliado. Artistas recebem sobre a receita total da cidade exclusiva. Influencers recebem sobre a receita dos estabelecimentos vinculados. Pagamentos realizados até o 5º dia útil do mês seguinte.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// APP METRICS TAB
// ─────────────────────────────────────────────

const APP_DATA = {
  totalUsers:       14832,
  activeUsers:       6210,
  newUsersThisMonth: 1847,
  newUsersLastMonth: 1523,
  premiumUsers:      2940,
  freeUsers:        11892,
  conversionRate:    19.8,
  totalCheckins:    38440,
  totalMessages:   126800,
  avgMessagesPerUser: 8.6,
  monthlyCheckins: [3200, 4100, 5200, 6400, 7800, 38440],
  monthlyMessages: [8400, 12100, 18400, 24600, 32100, 126800],
  monthlyNewUsers: [620, 890, 1120, 1380, 1523, 1847],
  cityData: [
    { city: "São Paulo",        users: 5840, checkins: 14200, premium: 1180, color: "#3B82F6" },
    { city: "Rio de Janeiro",   users: 3210, checkins:  8400, premium:  680, color: "#8B5CF6" },
    { city: "Belo Horizonte",   users: 1840, checkins:  4200, premium:  320, color: "#10B981" },
    { city: "Curitiba",         users: 1420, checkins:  3100, premium:  240, color: "#F59E0B" },
    { city: "Porto Alegre",     users: 1280, checkins:  2800, premium:  210, color: "#EF4444" },
    { city: "Outras",           users: 1242, checkins:  5740, premium:  310, color: "#6B7280" },
  ],
  venueCheckins: [
    { name: "Ballroom RJ",      city: "Rio de Janeiro", checkins: 4820, engagementRate: 74 },
    { name: "Club Noir",        city: "São Paulo",      checkins: 3940, engagementRate: 68 },
    { name: "Lux Underground",  city: "São Paulo",      checkins: 3210, engagementRate: 62 },
    { name: "Neon Garden",      city: "Porto Alegre",   checkins: 2180, engagementRate: 71 },
    { name: "Aurora Club",      city: "Curitiba",       checkins: 1840, engagementRate: 59 },
    { name: "The Basement",     city: "Belo Horizonte", checkins: 1430, engagementRate: 55 },
  ],
};

function AppMetricsTab() {
  const [period, setPeriod] = useState("month");
  const growth = (APP_DATA.newUsersThisMonth - APP_DATA.newUsersLastMonth) / APP_DATA.newUsersLastMonth * 100;

  const MiniLineChart = ({ data, color }) => {
    const max = Math.max(...data, 1);
    const w = 140, h = 48;
    const pts = data.map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - (v / max) * h;
      return `${x},${y}`;
    }).join(" ");
    return (
      <svg width={w} height={h} style={{ overflow: "visible" }}>
        <defs>
          <linearGradient id={"grad-" + color.replace("#","")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3"/>
            <stop offset="100%" stopColor={color} stopOpacity="0"/>
          </linearGradient>
        </defs>
        <polygon points={`0,${h} ${pts} ${w},${h}`} fill={"url(#grad-" + color.replace("#","") + ")"} />
        <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        {data.map((v, i) => {
          const x = (i / (data.length - 1)) * w;
          const y = h - (v / max) * h;
          return i === data.length - 1 ? <circle key={i} cx={x} cy={y} r={4} fill={color} /> : null;
        })}
      </svg>
    );
  };

  const KpiCard = ({ icon, label, value, sub, color, chart }) => (
    <div className="stat-card" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: T.textMid, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>{icon} {label}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.text, fontFamily: "'DM Serif Display', serif", letterSpacing: -0.5 }}>{value}</div>
          {sub && <div style={{ fontSize: 12, color: color || T.textMid, marginTop: 4 }}>{sub}</div>}
        </div>
        {chart && <div style={{ opacity: 0.9 }}>{chart}</div>}
      </div>
    </div>
  );

  const maxCityUsers = Math.max(...APP_DATA.cityData.map(c => c.users));

  return (
    <div className="fade-up">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, marginBottom: 4 }}>Métricas do App</h1>
          <p style={{ color: T.textMid, fontSize: 14 }}>Visão consolidada de usuários, check-ins e engajamento</p>
        </div>
        <div style={{ display: "flex", gap: 6, background: T.surface, padding: 4, borderRadius: 12, border: `1px solid ${T.border}` }}>
          {["week","month","all"].map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding: "6px 14px", borderRadius: 8, border: "none",
              background: period === p ? T.gold : "transparent",
              color: period === p ? "#000" : T.textMid,
              fontWeight: 700, fontSize: 12, cursor: "pointer",
            }}>
              {p === "week" ? "7d" : p === "month" ? "30d" : "Total"}
            </button>
          ))}
        </div>
      </div>

      {/* KPI row 1 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 16 }}>
        <KpiCard icon="👤" label="Usuários totais" value={APP_DATA.totalUsers.toLocaleString("pt-BR")} sub={`+${APP_DATA.newUsersThisMonth.toLocaleString("pt-BR")} este mês`} color={T.green} chart={<MiniLineChart data={APP_DATA.monthlyNewUsers} color={T.green} />} />
        <KpiCard icon="🟢" label="Usuários ativos" value={APP_DATA.activeUsers.toLocaleString("pt-BR")} sub={`${((APP_DATA.activeUsers/APP_DATA.totalUsers)*100).toFixed(0)}% do total`} color={T.blue} chart={<MiniLineChart data={[3100,3600,4200,4900,5600,APP_DATA.activeUsers]} color={T.blue} />} />
        <KpiCard icon="💎" label="Usuários Premium" value={APP_DATA.premiumUsers.toLocaleString("pt-BR")} sub={`Conversão: ${APP_DATA.conversionRate}%`} color={T.gold} chart={<MiniLineChart data={[800,1100,1500,1900,2400,APP_DATA.premiumUsers]} color={T.gold} />} />
        <KpiCard icon="📈" label="Novos este mês" value={APP_DATA.newUsersThisMonth.toLocaleString("pt-BR")} sub={`${growth > 0 ? "↑" : "↓"} ${Math.abs(growth).toFixed(0)}% vs. mês anterior`} color={growth > 0 ? T.green : T.red} chart={<MiniLineChart data={APP_DATA.monthlyNewUsers} color={growth > 0 ? T.green : T.red} />} />
      </div>

      {/* KPI row 2 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 24 }}>
        <KpiCard icon="📍" label="Check-ins totais" value={APP_DATA.totalCheckins.toLocaleString("pt-BR")} sub="Em todos os estabelecimentos" chart={<MiniLineChart data={APP_DATA.monthlyCheckins} color={T.purple} />} />
        <KpiCard icon="💬" label="Mensagens enviadas" value={APP_DATA.totalMessages.toLocaleString("pt-BR")} sub={`Média: ${APP_DATA.avgMessagesPerUser} por usuário`} chart={<MiniLineChart data={APP_DATA.monthlyMessages} color={T.blue} />} />
        <div className="stat-card" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: 20 }}>
          <div style={{ fontSize: 12, color: T.textMid, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>🔄 Free → Premium</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.gold, fontFamily: "'DM Serif Display', serif" }}>{APP_DATA.conversionRate}%</div>
              <div style={{ fontSize: 12, color: T.textMid, marginTop: 4 }}>Taxa de conversão</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 13, color: T.textMid, marginBottom: 4 }}>Free: <strong style={{ color: T.text }}>{APP_DATA.freeUsers.toLocaleString("pt-BR")}</strong></div>
              <div style={{ fontSize: 13, color: T.textMid }}>Premium: <strong style={{ color: T.gold }}>{APP_DATA.premiumUsers.toLocaleString("pt-BR")}</strong></div>
            </div>
          </div>
          <div style={{ marginTop: 12, height: 6, background: T.border, borderRadius: 6, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${APP_DATA.conversionRate}%`, background: `linear-gradient(90deg, ${T.gold}, ${T.green})`, borderRadius: 6 }} />
          </div>
        </div>
      </div>

      {/* Two columns: city map + venue table */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>

        {/* City distribution */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, fontSize: 14, fontWeight: 700 }}>
            🏙️ Usuários por Cidade
          </div>
          <div style={{ padding: "8px 0" }}>
            {APP_DATA.cityData.map(c => (
              <div key={c.city} style={{ padding: "10px 20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: c.color }} />
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{c.city}</span>
                  </div>
                  <div style={{ display: "flex", gap: 16, fontSize: 13 }}>
                    <span style={{ color: T.text }}>{c.users.toLocaleString("pt-BR")}</span>
                    <span style={{ color: T.gold }}>{c.premium} 💎</span>
                  </div>
                </div>
                <div style={{ height: 4, background: T.border, borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${(c.users / maxCityUsers) * 100}%`, background: c.color, borderRadius: 4, opacity: 0.8 }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, color: T.textDim }}>
                  <span>{c.checkins.toLocaleString("pt-BR")} check-ins</span>
                  <span>{((c.premium/c.users)*100).toFixed(0)}% premium</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Venue check-ins */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, fontSize: 14, fontWeight: 700 }}>
            🏠 Check-ins por Estabelecimento
          </div>
          <div style={{ padding: "12px 0" }}>
            {APP_DATA.venueCheckins.map((v, i) => {
              const maxC = Math.max(...APP_DATA.venueCheckins.map(x => x.checkins));
              return (
                <div key={v.name} style={{ padding: "10px 20px", borderBottom: i < APP_DATA.venueCheckins.length - 1 ? `1px solid ${T.border}` : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{v.name}</div>
                      <div style={{ fontSize: 11, color: T.textDim }}>{v.city}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{v.checkins.toLocaleString("pt-BR")}</div>
                      <div style={{ fontSize: 11, color: v.engagementRate >= 65 ? T.green : T.textMid }}>{v.engagementRate}% engaj.</div>
                    </div>
                  </div>
                  <div style={{ height: 4, background: T.border, borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${(v.checkins / maxC) * 100}%`, background: `linear-gradient(90deg, ${T.blue}, ${T.purple})`, borderRadius: 4 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Monthly trend table */}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, fontSize: 14, fontWeight: 700 }}>
          📅 Evolução Mensal
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr repeat(6,1fr)", padding: "10px 20px", borderBottom: `1px solid ${T.border}`, fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.8 }}>
          <span>Métrica</span>
          {["Set","Out","Nov","Dez","Jan","Fev"].map(m => <span key={m} style={{ textAlign: "right" }}>{m}</span>)}
        </div>
        {[
          { label: "Novos usuários", data: APP_DATA.monthlyNewUsers, color: T.green },
          { label: "Check-ins",      data: APP_DATA.monthlyCheckins, color: T.purple },
          { label: "Mensagens",      data: APP_DATA.monthlyMessages, color: T.blue  },
        ].map(row => {
          const max = Math.max(...row.data);
          return (
            <div key={row.label} className="row-hover" style={{ display: "grid", gridTemplateColumns: "1fr repeat(6,1fr)", padding: "12px 20px", borderBottom: `1px solid ${T.border}`, alignItems: "center" }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: row.color }}>{row.label}</span>
              {row.data.map((v, i) => (
                <div key={i} style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 13, fontWeight: i === row.data.length - 1 ? 800 : 400, color: i === row.data.length - 1 ? row.color : T.textMid }}>
                    {v >= 1000 ? (v/1000).toFixed(1) + "k" : v}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────────
export default function EATAffiliates() {
  const [affiliates, setAffiliates] = useState(ALL_AFFILIATES);
  const [tab, setTab] = useState("overview");          // overview | affiliates | commission
  const [filter, setFilter] = useState({ type: "all", status: "all", city: "all", search: "" });
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const updateStatus = (id, status) => {
    setAffiliates(prev => prev.map(a => a.id === id ? { ...a, status } : a));
    setSelected(null);
  };

  const addAffiliate = (aff) => setAffiliates(prev => [...prev, aff]);

  const filtered = affiliates.filter(a => {
    if (filter.type !== "all" && a.type !== filter.type) return false;
    if (filter.status !== "all" && a.status !== filter.status) return false;
    if (filter.city !== "all" && a.city !== filter.city) return false;
    if (filter.search && !a.name.toLowerCase().includes(filter.search.toLowerCase())) return false;
    return true;
  });

  // global stats
  const activeAff = affiliates.filter(a => a.status === "active");
  const totalComm = activeAff.reduce((s, a) => s + calcCommission(getAffiliateRevenue(a)), 0);
  const totalRev  = activeAff.reduce((s, a) => s + getAffiliateRevenue(a), 0);
  const artists   = affiliates.filter(a => a.type === "artist").length;
  const influencers = affiliates.filter(a => a.type === "influencer").length;

  const TABS = [
    { id: "overview",    label: "Visão Geral"   },
    { id: "affiliates",  label: "Afiliados"      },
    { id: "commission",  label: "Comissões"      },
    { id: "app",         label: "📱 App"         },
  ];

  return (
    <>
      <style>{css}</style>
      <div style={{ minHeight: "100vh", background: T.bg, padding: "0 0 60px" }}>

        {/* TOP BAR */}
        <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}`, padding: "0 32px" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: `linear-gradient(135deg, ${T.gold}, #8B7020)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'DM Serif Display', serif", fontSize: 18, color: "#000", fontWeight: 700,
              }}>E</div>
              <div>
                <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18, letterSpacing: -0.3 }}>EAT</div>
                <div style={{ fontSize: 11, color: T.textDim, marginTop: -2 }}>Painel de Afiliados</div>
              </div>
            </div>

            <nav style={{ display: "flex", gap: 4 }}>
              {TABS.map(t => (
                <button key={t.id} className="tab-btn" onClick={() => setTab(t.id)} style={{
                  padding: "8px 18px", borderRadius: 10, border: "none",
                  background: tab === t.id ? `${T.gold}18` : "transparent",
                  color: tab === t.id ? T.gold : T.textMid,
                  fontWeight: tab === t.id ? 700 : 500,
                  fontSize: 14, cursor: "pointer",
                  borderBottom: tab === t.id ? `2px solid ${T.gold}` : "2px solid transparent",
                }}>
                  {t.label}
                </button>
              ))}
            </nav>

            <button className="btn-primary pulse-gold" onClick={() => setShowNew(true)}>
              + Novo Afiliado
            </button>
          </div>
        </div>

        {/* CONTENT */}
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 32px 0" }}>

          {/* ── OVERVIEW TAB ── */}
          {tab === "overview" && (
            <div>
              <div style={{ marginBottom: 28 }}>
                <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, marginBottom: 6 }}>Visão Geral</h1>
                <p style={{ color: T.textMid, fontSize: 15 }}>Programa de afiliados EAT · Fevereiro 2026</p>
              </div>

              {/* stat cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 32 }}>
                <StatCard icon="👥" label="Afiliados ativos" value={activeAff.length} sub={`+${affiliates.filter(a=>a.status==="pending").length} pendentes`} delay="" />
                <StatCard icon="🎵" label="Artistas / Influencers" value={`${artists} / ${influencers}`} sub="por tipo" delay="-1" />
                <StatCard icon="💰" label="Comissões a pagar" value={fmt(totalComm)} sub="Mês atual" accent={T.gold} delay="-2" />
                <StatCard icon="📈" label="Receita base total" value={fmt(totalRev)} sub="Assinaturas" accent={T.green} delay="-3" />
              </div>

              {/* two columns */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>

                {/* top affiliates */}
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, fontSize: 14, fontWeight: 700 }}>
                    🏆 Top Afiliados por Comissão
                  </div>
                  {activeAff
                    .map(a => ({ a, c: calcCommission(getAffiliateRevenue(a)) }))
                    .sort((x, y) => y.c - x.c)
                    .slice(0, 5)
                    .map(({ a, c }, i) => (
                      <div key={a.id} className="row-hover" onClick={() => setSelected(a)} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 20px", borderBottom: `1px solid ${T.border}`, cursor: "pointer" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: i === 0 ? T.gold : T.border, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: i === 0 ? "#000" : T.textMid, flexShrink: 0 }}>
                          {i + 1}
                        </div>
                        <span style={{ fontSize: 22 }}>{a.avatar}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{a.name}</div>
                          <div style={{ fontSize: 12, color: T.textDim }}>{a.type === "artist" ? a.city : `${a.venues.length} locais`}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontWeight: 700, color: T.gold, fontSize: 14 }}>{fmt(c)}</div>
                          <div style={{ fontSize: 11, color: T.textDim }}>comissão</div>
                        </div>
                      </div>
                    ))}
                </div>

                {/* venues performance */}
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, fontSize: 14, fontWeight: 700 }}>
                    🏠 Receita por Estabelecimento
                  </div>
                  {[...VENUES].sort((a, b) => b.monthlyRevenue - a.monthlyRevenue).map((v, i) => {
                    const maxRev = Math.max(...VENUES.map(x => x.monthlyRevenue));
                    return (
                      <div key={v.id} style={{ padding: "10px 20px", borderBottom: `1px solid ${T.border}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 14 }}>
                          <span style={{ fontWeight: 600 }}>{v.name}</span>
                          <span style={{ color: T.green }}>{fmt(v.monthlyRevenue)}</span>
                        </div>
                        <div style={{ height: 4, background: T.border, borderRadius: 4, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${(v.monthlyRevenue / maxRev) * 100}%`, background: `linear-gradient(90deg, ${T.blue}, ${T.purple})`, borderRadius: 4, transition: "width 0.8s ease" }} />
                        </div>
                        <div style={{ fontSize: 11, color: T.textDim, marginTop: 4 }}>{v.city}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* city cards */}
              <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, fontSize: 14, fontWeight: 700 }}>
                  🏙️ Receita por Cidade
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 0 }}>
                  {CITIES.map((city, i) => {
                    const cityVenues = VENUES.filter(v => v.city === city);
                    const cityRev = cityVenues.reduce((s, v) => s + v.monthlyRevenue, 0);
                    const cityArtists = affiliates.filter(a => a.type === "artist" && a.city === city && a.status === "active");
                    const cityInflu = affiliates.filter(a => a.type === "influencer" && a.city === city && a.status === "active");
                    return (
                      <div key={city} style={{ padding: "20px 20px", borderRight: i < 4 ? `1px solid ${T.border}` : "none" }}>
                        <div style={{ fontSize: 12, color: T.textDim, marginBottom: 4 }}>{city}</div>
                        <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'DM Serif Display', serif", color: cityRev > 0 ? T.text : T.textDim }}>
                          {cityRev > 0 ? fmtShort(cityRev) : "—"}
                        </div>
                        <div style={{ fontSize: 11, color: T.textMid, marginTop: 6 }}>
                          {cityArtists.length > 0 && <span style={{ color: T.purple }}>🎵 {cityArtists.length} artista{cityArtists.length > 1 ? "s" : ""} · </span>}
                          {cityInflu.length > 0 && <span style={{ color: T.gold }}>⭐ {cityInflu.length} influencer{cityInflu.length > 1 ? "s" : ""}</span>}
                          {cityArtists.length === 0 && cityInflu.length === 0 && <span style={{ color: T.textDim }}>Sem afiliados ativos</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── AFFILIATES TAB ── */}
          {tab === "affiliates" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                <div>
                  <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, marginBottom: 4 }}>Afiliados</h1>
                  <p style={{ color: T.textMid, fontSize: 14 }}>{filtered.length} de {affiliates.length} afiliados</p>
                </div>
              </div>

              {/* filters */}
              <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
                <input
                  placeholder="🔍 Buscar afiliado..."
                  value={filter.search}
                  onChange={e => setFilter({ ...filter, search: e.target.value })}
                  style={{ flex: 1, minWidth: 200 }}
                />
                <select value={filter.type} onChange={e => setFilter({ ...filter, type: e.target.value })} style={{ minWidth: 160 }}>
                  <option value="all">Todos os tipos</option>
                  <option value="artist">🎵 Artista</option>
                  <option value="influencer">⭐ Influencer</option>
                </select>
                <select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })} style={{ minWidth: 150 }}>
                  <option value="all">Todos os status</option>
                  <option value="active">Ativo</option>
                  <option value="pending">Pendente</option>
                  <option value="inactive">Inativo</option>
                </select>
                <select value={filter.city} onChange={e => setFilter({ ...filter, city: e.target.value })} style={{ minWidth: 170 }}>
                  <option value="all">Todas as cidades</option>
                  {CITIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>

              {/* grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px,1fr))", gap: 16 }}>
                {filtered.map((aff, i) => {
                  const revenue = getAffiliateRevenue(aff);
                  const comm = calcCommission(revenue);
                  const lastMonth = aff.monthlyHistory[aff.monthlyHistory.length - 1];
                  const prevMonth = aff.monthlyHistory[aff.monthlyHistory.length - 2];
                  const growth = prevMonth > 0 ? ((lastMonth - prevMonth) / prevMonth * 100).toFixed(0) : null;
                  return (
                    <div key={aff.id} className="stat-card fade-up" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: 20, cursor: "pointer" }} onClick={() => setSelected(aff)}>
                      {/* card header */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ width: 48, height: 48, borderRadius: "50%", background: `linear-gradient(135deg, ${T.gold}22, ${T.purple}22)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, border: `1.5px solid ${T.borderHi}` }}>
                            {aff.avatar}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 15 }}>{aff.name}</div>
                            <div style={{ fontSize: 12, color: T.textMid, marginTop: 2 }}>
                              {aff.instagramFollowers.toLocaleString("pt-BR")} seguidores
                            </div>
                          </div>
                        </div>
                        <StatusBadge status={aff.status} />
                      </div>

                      {/* type + location */}
                      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                        <TypeBadge type={aff.type} />
                        <span className="badge" style={{ background: T.surface, color: T.textMid }}>
                          {aff.type === "artist" ? `🏙 ${aff.city}` : `🏠 ${aff.venues.length} local${aff.venues.length !== 1 ? "is" : ""}`}
                        </span>
                      </div>

                      {/* mini chart */}
                      <MiniBar data={aff.monthlyHistory} color={aff.status === "active" ? T.gold : T.textDim} height={36} />

                      {/* metrics */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
                        <div style={{ background: T.surface, borderRadius: 10, padding: "10px 14px" }}>
                          <div style={{ fontSize: 11, color: T.textMid, marginBottom: 2 }}>Receita base</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: aff.status === "active" ? T.text : T.textDim }}>
                            {aff.status === "active" ? fmt(revenue) : "—"}
                          </div>
                        </div>
                        <div style={{ background: `${T.gold}0D`, borderRadius: 10, padding: "10px 14px" }}>
                          <div style={{ fontSize: 11, color: T.textMid, marginBottom: 2 }}>Comissão (6%)</div>
                          <div style={{ fontSize: 14, fontWeight: 800, color: aff.status === "active" ? T.gold : T.textDim }}>
                            {aff.status === "active" ? fmt(comm) : "—"}
                          </div>
                        </div>
                      </div>

                      {growth !== null && aff.status === "active" && (
                        <div style={{ marginTop: 10, fontSize: 12, color: parseInt(growth) >= 0 ? T.green : T.red }}>
                          {parseInt(growth) >= 0 ? "↑" : "↓"} {Math.abs(growth)}% vs. mês anterior
                        </div>
                      )}
                    </div>
                  );
                })}
                {filtered.length === 0 && (
                  <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 60, color: T.textDim, fontSize: 15 }}>
                    Nenhum afiliado encontrado com os filtros aplicados.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── COMMISSION TAB ── */}
          {tab === "commission" && (
            <div>
              <div style={{ marginBottom: 24 }}>
                <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, marginBottom: 4 }}>Comissões</h1>
                <p style={{ color: T.textMid, fontSize: 14 }}>Cálculo detalhado com dedução de impostos e taxas de cartão</p>
              </div>
              <CommissionReport affiliates={affiliates} />
            </div>
          )}


          {/* ── APP METRICS TAB ── */}
          {tab === "app" && (
            <AppMetricsTab />
          )}

        </div>
      </div>

      {/* MODALS */}
      {selected && (
        <AffiliateModal
          aff={selected}
          onClose={() => setSelected(null)}
          onStatusChange={updateStatus}
        />
      )}
      {showNew && (
        <NewAffiliateModal
          onClose={() => setShowNew(false)}
          onAdd={addAffiliate}
        />
      )}
    </>
  );
}
